import * as React from 'react'
import {useCallback} from 'react'

import {EntityProvider} from '../../../../contexts/entity'
import {stringifyId} from '../../../../types'
import {toGroups} from '../../../../utils/array'
import Grid from '../../Grid/Grid'
import SequenceLoader from '../../SequenceLoader/SequenceLoader'
import Visible from '../../Visible/Visible'
import ShowAllBuildTypes from '../ShowAllBuildTypes/ShowAllBuildTypes'

import type {Props} from './BuildTypes.types'
import {NestedBuildTypes} from './BuildTypesContext'

import styles from './BuildTypes.css'

const GROUP_SIZE = 10
export default function BuildTypes({
  overviewBuildTypeIds,
  buildTypeIds,
  onlyFavorites = false,
  hasFavorites,
  archivedProject,
  gridClassName,
}: Props) {
  const [showAll, setShowAll] = React.useReducer(() => true, false)
  const showOnlyFavorites = onlyFavorites || (hasFavorites && !showAll)
  const sortedBuildTypeIds = showOnlyFavorites
    ? overviewBuildTypeIds
    : [...overviewBuildTypeIds, ...buildTypeIds.filter(id => !overviewBuildTypeIds.includes(id))]
  const groups = toGroups(GROUP_SIZE, sortedBuildTypeIds)
  const renderBuildTypeLines = useCallback(
    (ids: readonly string[]) => (
      <>
        <SequenceLoader
          showFirstLoadingItem
          waitForAllLoaded
          className={styles.list}
          itemClassName={styles.item}
        >
          {ids.map(buildTypeId => (
            <EntityProvider
              key={stringifyId(buildTypeId)}
              seqKey={stringifyId(buildTypeId)}
              buildTypeId={buildTypeId}
            >
              <NestedBuildTypes
                buildTypeId={buildTypeId}
                archivedProject={archivedProject}
                isFavorite={showOnlyFavorites || overviewBuildTypeIds.includes(buildTypeId)}
              />
            </EntityProvider>
          ))}
        </SequenceLoader>
      </>
    ),
    [archivedProject, overviewBuildTypeIds, showOnlyFavorites],
  )
  return (
    <SequenceLoader showFirstLoadingItem withIncrementalRendering>
      {groups.map((ids, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Visible key={i} seqKey={String(i)}>
          {() =>
            gridClassName ? (
              <Grid className={gridClassName}>{renderBuildTypeLines(ids)}</Grid>
            ) : (
              <div className={styles.container}>{renderBuildTypeLines(ids)}</div>
            )
          }
        </Visible>
      ))}
      {!onlyFavorites && showOnlyFavorites && buildTypeIds.length > overviewBuildTypeIds.length ? (
        <ShowAllBuildTypes seqKey="showAll" onClick={setShowAll} count={buildTypeIds.length} />
      ) : null}
    </SequenceLoader>
  )
}
